<template>
  <!-- N.B. since the element is whitespace: pre-wrap, all the children must be on one line of html to prevent leading line break-->
  <div class="rich-text-editor">
    <input :id="uniqueId" type="hidden" :value="value" /><trix-editor
      ref="editor"
      :toolbar="toolbarId"
      :input="uniqueId"
      :aria-label="ariaLabel"
      :placeholder="placeholder"
      :dir="dir"
      @paste="autolinkPastedUrl"
      @trix-initialize="onInitialize"
      @trix-change="onInput"
      @trix-selection-change="onSelectionChange"
      @trix-attributes-change="onAttributesChange"
      @trix-file-accept="onFileAccept"
      @trix-attachment-add="onAttachmentAdd"
      @focusin="onFocusIn"
      @trix-focus="onFocus"
      @trix-blur="onBlur"
      @keydown.enter.exact="onEnter"
      @keydown.down.exact="onArrowDown"
      @keydown.up.exact="onArrowUp"
      @paste.stop="$emit('paste', $event)"
    />
  </div>
</template>

<script>
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import uniqueId from 'lodash.uniqueid'
import Trix from 'trix'

const urlRegex = new RegExp(
  '^(https?:\\/\\/(?:www\\.|(?!www))[^\\s\\.]+\\.[^\\s]{2,}|www\\.[^\\s]+\\.[^\\s]{2,})$',
  'ig',
)

Trix.config.textAttributes.sup = {
  tagName: 'sup',
  inheritable: true,
}

Trix.config.textAttributes.sub = {
  tagName: 'sub',
  inheritable: true,
}

Trix.config.textAttributes.mark = {
  tagName: 'mark',
  inheritable: true,
}

Trix.config.blockAttributes.math = {
  tagName: 'var',
  terminal: true,
  text: {
    plaintext: true,
  },
}
// emitted events: initialize, focus, selectionchange, attributeschange, input [html]
export default {
  props: ['placeholder', 'value', 'dir', 'toolbarId', 'ariaLabel'],
  data() {
    return {
      editor: null,
      uniqueId: uniqueId('rte'),
    }
  },
  watch: {
    value(newValue) {
      if (this.editor && this.html() !== newValue) {
        this.editor.loadHTML(newValue)
      }
    },
  },
  methods: {
    freezeSelection() {
      this.editor.activateAttribute('frozen')
      this.frozenSelectionRange = this.editor.getSelectedRange()
    },
    defreezeSelection() {
      if (this.frozenSelectionRange) {
        this.editor.setSelectedRange(this.frozenSelectionRange)
        this.frozenSelectionRange = null
      }
      this.editor && this.editor.deactivateAttribute('frozen')
    },
    focus() {
      // nextTick - ensures that the input element has completed rendering in the DOM
      // setTimeout -  defers the focusing event until after the current event propagation.
      //
      // If you click on a button that triggers this method and don't have setTimeout,
      // what can happen is: focus is shifted to this input, then immediately shifted to the button.
      this.$nextTick(() => {
        setTimeout(() => {
          // if the post is deleted remotely, this.$refs.editor will become undefined
          if (this.$refs.editor) {
            this.$refs.editor.focus()
          }
        })
      })
    },
    activateAttribute(name, val) {
      this.defreezeSelection()
      this.editor.activateAttribute(name, val)
      if (name === 'href') {
        this.moveCursorToEndOfSelection()
      }
    },
    deactivateAttribute(name) {
      this.editor.deactivateAttribute(name)
    },
    moveCursorToEndOfSelection() {
      const currentSelectedRange = this.editor.getSelectedRange()
      if (currentSelectedRange && currentSelectedRange[0] !== currentSelectedRange[1]) {
        this.editor.setSelectedRange(currentSelectedRange[1])
      }
    },
    html() {
      return this.$refs.editor?.value || ''
    },
    activeAttributes() {
      return this.editor.composition.currentAttributes
    },
    selectedText() {
      if (!this.editor) return ''
      if (!this.editor.getDocument) return ''
      const currentSelection = this.editor.getSelectedRange()
      if (!currentSelection) return ''
      return this.editor.getDocument().getStringAtRange(currentSelection)
    },
    selectionCoordinates() {
      if (!this.editor) return {}
      if (!this.editor.getDocument) return {}
      const currentSelection = this.editor.getSelectedRange()
      if (!currentSelection) return {}
      const clientRect = this.editor.getClientRectAtPosition(currentSelection[0])
      if (clientRect) {
        return { x: clientRect.x, y: clientRect.y }
      } else {
        return {}
      }
    },
    onInitialize(event) {
      this.editor = event.target.editor
      this.editor.loadHTML(this.value)
      this.$emit('initialize')
    },
    onFocus() {
      this.$emit('focus')
    },
    onBlur() {
      this.$emit('blur')
    },
    onFocusIn() {
      this.defreezeSelection()
    },
    onSelectionChange() {
      this.$emit('selectionchange', this.selectedText(), this.selectionCoordinates())
    },
    onAttributesChange() {
      this.$emit('attributeschange', this.activeAttributes())
    },
    onInput() {
      this.$emit('input', this.html())
    },
    onFileAccept(e) {
      this.$emit('fileaccept', e)
    },
    onEnter() {
      this.$emit('enter')
    },
    onAttachmentAdd(e) {
      if (e.attachment) {
        e.attachment.remove()
      }

      // If jQuery
      if (e.originalEvent && e.originalEvent.attachment) {
        e.originalEvent.attachment.remove()
      }
    },
    autolinkPastedUrl(e) {
      setTimeout(() => {
        const clip = e.clipboardData
        let pastedText = null
        if (clip && typeof clip.getData === 'function') {
          pastedText = clip.getData('Text')
        }
        if (pastedText) {
          if (pastedText.match(urlRegex)) {
            const element = e.currentTarget
            const editor = element.editor
            const currentText = editor.getDocument().toString()
            const currentSelection = element.editor.getSelectedRange()
            // text up to the cursor position
            const textWeAreInterestedIn = currentText.substring(0, currentSelection[0])
            // search for the start of the url
            const startOfPastedText = textWeAreInterestedIn.lastIndexOf(pastedText)
            // add an undo entry so people can undo the autolinking
            editor.recordUndoEntry('Auto Link Paste')
            // select the url text
            editor.setSelectedRange([startOfPastedText, currentSelection[0]])
            // add a hyperlink to it
            editor.activateAttribute('href', pastedText)
            // go back to the original selection
            return editor.setSelectedRange(currentSelection)
          }
        }
        return true
      }, 0)
    },
    onArrowDown() {
      // emit an event when arrow down key is pressed and the cursor is on the last line of the editor
      // the default behaviour for arrow down on the last line is move to the end of the line
      // we only emit a special event to be handled if the cursor is already at the end of the last line => check this.editor.getSelectedRange()[0] === value.length
      if (this.editor.getSelectedRange()[0] === this.editor.getDocument().toString().length - 1) {
        this.$emit('arrow-down-on-last-line')
      }
    },
    onArrowUp() {
      // emit an event when arrow up key is pressed and the cursor is on the first line of the editor
      // the default behaviour for arrow up on the first line is move to the start of the line
      // we only emit a special event to be handled if the cursor is already at the start of the first line => check this.editor.getSelectedRange()[0] === 0
      if (this.editor.getSelectedRange()[0] === 0) {
        this.$emit('arrow-up-on-first-line')
      }
    },
  },
}
</script>

<style lang="postcss" scoped>
// We have to add style for trix editor/toolbar here

trix-editor {
  min-height: 24px;
}

// Placeholder text
trix-editor:empty:not(:focus)::before {
  .rich-text-editor:not(.new-post-modal-editor) & {
    color: rgba(0, 0, 0, 0.38);
    [data-color-scheme='dark'] & {
      color: rgba(255, 255, 255, 0.5);
    }
  }
  .new-post-modal-editor & {
    @apply text-dark-text-tertiary;
    @apply dark:text-light-text-tertiary;
  }
}
</style>

<style lang="scss">
// Custom styles for trix toolbar
@import '~styles/3/partials/fonts/oricons';
@import './../../assets/stylesheets/3/modules/all';

// ---------------------------------------
// STYLES COPIED FROM THE ORIGINAL TOOLBAR
// ---------------------------------------

/*
Trix 0.11.1
Copyright © 2017 Basecamp, LLC
http://trix-editor.org/*/

trix-toolbar * {
  box-sizing: border-box;
}

trix-toolbar .trix-button-row {
  display: flex;
  flex-wrap: nowrap;
  justify-content: space-between;
}

trix-toolbar .trix-button-group {
  display: flex;
  margin-bottom: 10px;
  border: 1px solid #bbb;
  border-top-color: #ccc;
  border-bottom-color: #888;
  border-radius: 3px;
}

trix-toolbar .trix-button {
  position: relative;
  float: left;
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.75em;
  font-weight: 600;
  white-space: nowrap;
  padding: 0 0.5em;
  margin: 0;
  outline: none;
  border: none;
  border-bottom: 1px solid #ddd;
  border-radius: 0;
  background: transparent;
}

trix-toolbar .trix-button:not(:first-child) {
  border-left: 1px solid #ccc;
}

trix-toolbar .trix-button.trix-active {
  background: #cbeefa;
  color: black;
}

trix-toolbar .trix-button:not(:disabled) {
  cursor: pointer;
}

trix-toolbar .trix-button:disabled {
  color: rgba(0, 0, 0, 0.125);
}

@media (max-device-width: 768px) {
  trix-toolbar .trix-button {
    letter-spacing: -0.01em;
    padding: 0 0.3em;
  }
}

trix-toolbar .trix-button--icon {
  font-size: inherit;
  width: 2.6em;
  height: 1.6em;
  max-width: calc(0.8em + 4vw);
  text-indent: -9999px;
}

@media (max-device-width: 768px) {
  trix-toolbar .trix-button--icon {
    height: 2em;
    max-width: calc(0.8em + 3.5vw);
  }
}

trix-toolbar .trix-button--icon::before {
  display: inline-block;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  content: '';
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
}

@media (max-device-width: 768px) {
  trix-toolbar .trix-button--icon::before {
    right: 6%;
    left: 6%;
  }
}

trix-toolbar .trix-button--icon.trix-active::before {
  opacity: 1;
}

trix-toolbar .trix-button--icon:disabled::before {
  opacity: 0.125;
}

trix-toolbar .trix-dialogs {
  position: relative;
}

trix-toolbar .trix-dialog {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  font-size: 0.75em;
  padding: 15px 10px;
  background: #fff;
  box-shadow: 0 0.3em 1em #ccc;
  border-top: 2px solid #888;
  border-radius: 5px;
  z-index: 5;
}

trix-toolbar .trix-input--dialog {
  font-size: inherit;
  font-weight: normal;
  padding: 0.5em 0.8em;
  margin: 0 10px 0 0;
  border-radius: 3px;
  border: 1px solid #bbb;
  background-color: #fff;
  box-shadow: none;
  outline: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}

trix-toolbar .trix-input--dialog.validate:invalid {
  box-shadow: #f00 0px 0px 1.5px 1px;
}

trix-toolbar .trix-button--dialog {
  font-size: inherit;
  padding: 0.5em;
  border-bottom: none;
}

trix-toolbar .trix-dialog--link {
  @apply max-w-screen-lg;
}

trix-toolbar .trix-dialog__link-fields {
  display: flex;
  align-items: baseline;
}

trix-toolbar .trix-dialog__link-fields .trix-input {
  flex: 1;
}

trix-toolbar .trix-dialog__link-fields .trix-button-group {
  flex: 0 0 content;
  margin: 0;
}

// ---------------------
// PADLET CUSTOMIZATIONS
// ---------------------

trix-toolbar {
  @apply bg-light-ui-150 dark:bg-dark-ui-150;
  @apply z-formatting-toolbar;
  @apply box-border;
  @apply text-center;
  @apply absolute;
  @apply left-0;
  @apply top-0;

  .trix-toolbar-custom-separator {
    @apply relative;

    &::after {
      content: '';
      border-left: 1.25px solid;
      @apply border-dark-text-400 dark:border-light-text-400;
      @apply absolute;
      @apply top-2 md:top-3;
      @apply bottom-2 md:bottom-3;
      @apply right-0;
    }
  }

  &.toolbar-floating {
    @apply shadow-md;
    @apply rounded;
    @apply hidden;
    @apply whitespace-normal;
    @apply max-w-[calc(100%-16px)];
    @apply overflow-auto;
    .variant-android & {
      @apply w-screen;
      @apply overflow-auto;
    }
  }

  .trix-button-group {
    @apply border-none;
    @apply py-0.5 md:py-1;
    @apply px-0 md:px-5;
    @apply flex;
    @apply gap-x-0 md:gap-x-4;
    @apply overflow-hidden;
    @apply mb-0;
    @apply flex-shrink-0;
    @apply rounded-none;

    &.trix-button-group--history-tools {
      @apply hidden;
    }
  }

  .trix-button-group .trix-button {
    @apply text-light-ui-100;
    @apply border-l-0 border-b-0;

    &.trix-active {
      @apply bg-transparent;
      &::before {
        @apply opacity-100;
        @apply text-grape-500 dark:text-canary-500;
      }
    }

    &::disabled::before {
      @apply opacity-10;
    }
  }

  .trix-button--icon {
    text-indent: initial;
    font-size: 0;
    @apply w-8;
    @apply h-8 md:h-10;
    max-width: initial;
    @apply border-none;
    @apply m-0;
  }

  .trix-button--icon::before {
    @include oricon;
    @apply flex;
    @apply flex-col;
    @apply justify-center;
    @apply text-dark-text-100 dark:text-light-text-100;
    @apply text-14-19;
  }

  // combining tailwind's hover-hover, hover, before leads to some weird bugs, write the style like this for now
  @media (hover: hover) {
    .trix-button--icon:not(:disabled):hover::before {
      @apply text-grape-500 dark:text-canary-500;
    }

    .trix-button--icon:not(:disabled):active::before {
      @apply text-grape-500 dark:text-canary-500;
    }

    .trix-button--icon:not(:disabled):focus::before {
      @apply text-grape-500 dark:text-canary-500;
    }
  }

  .trix-button--icon-heading-1 {
    @apply hidden;
  }

  // CUSTOM ICONS

  .trix-button--icon-bold::before {
    content: 'bold';
  }

  .trix-button--icon-italic::before {
    content: 'italic';
  }

  .trix-button--icon-link::before {
    content: 'link_underlined';
  }

  .trix-button--icon-strike::before {
    content: 'strikethrough';
  }

  .trix-button--icon-sup::before {
    content: 'superscript';
  }

  .trix-button--icon-sub::before {
    content: 'subscript';
  }

  .trix-button--icon-mark::before {
    content: 'highlight_text';
  }

  .trix-button--icon-quote::before {
    content: 'quotemark';
  }

  .trix-button--icon-code::before {
    content: 'code';
  }

  .trix-button--icon-math::before {
    content: 'pi';
  }

  .trix-button--icon-bullet-list::before {
    content: 'bulleted_list';
  }

  .trix-button--icon-number-list::before {
    content: 'numbered_list';
  }

  .trix-button--icon-decrease-nesting-level::before {
    content: 'indent_decrease';
  }

  .trix-button--icon-increase-nesting-level::before {
    content: 'indent_increase';
  }

  .trix-button--icon-undo::before {
    content: 'undo';
  }

  .trix-button--icon-redo::before {
    content: 'redo';
  }

  .trix-dialogs {
    @apply relative;
    @apply top-0;
    @apply flex;
    @apply w-full;
    @apply justify-center;
  }

  .trix-dialogs .trix-dialog {
    @apply -mt-10 md:-mt-11;
    @apply w-full;
    @apply py-0;
    @apply px-1;
    @apply leading-3;
    @apply bg-light-ui-150 dark:bg-dark-ui-150;
    @apply rounded;
    @apply z-snackbar;
    @apply shadow-none;
    @apply border-t-0;
  }

  .trix-dialogs .trix-dialog__link-fields {
    @apply flex;
    @apply items-center;

    .trix-back-button.icon-button {
      @include oricon;
      @apply text-dark-text-200 dark:text-light-text-200;
    }
  }

  .trix-dialogs input[type='url'] {
    @apply flex-grow;
    @apply h-[26px];
    @apply text-14;
    @apply py-0;
    @apply px-2;
    @apply my-0;
    @apply mr-2;
    @apply ml-0;
    @apply border-none;
    @apply bg-transparent;
    @apply font-sans;
    @apply text-dark-text-100 dark:text-light-text-100;
    box-shadow: none !important;
    outline: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    @apply placeholder-dark-text-300 dark:placeholder-light-text-300;
  }

  .trix-dialogs input[type='button'] {
    @apply w-auto;
    @include common-button;
    @apply py-0;
    @apply px-2;

    &[data-trix-method='setAttribute'] {
      @apply text-padlet-pink-500;
      @apply hover-hover:hover:bg-padlet-pink-50;
      @apply rounded;
    }

    &[data-trix-method='removeAttribute'] {
      @apply text-dark-text-200 dark:text-light-text-200;
      @apply hover-hover:hover:bg-light-ui-300 hover-hover:dark:hover:bg-dark-ui-300;
      @apply rounded;
    }
  }

  &.toolbar-fixed {
    @apply hidden;
    @apply top-auto;
    @apply bottom-0;
    @apply left-0;
    @apply right-0;
    @apply fixed;
    @apply overflow-auto;
    -webkit-overflow-scrolling: touch;

    .trix-button-row {
      @apply justify-start;
    }

    .trix-button-group {
      @apply gap-x-4 md:gap-x-6;
    }
  }

  // we don't use the file tools in trix toolbar => hide it
  .trix-button-group.trix-button-group--file-tools {
    @apply hidden;
  }
}
</style>
